import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { useTeam } from 'team/contexts/teamContext';
import TeamSidenav from 'team/components/TeamSidenav';
import TeamHomeContainer from 'team/pages/TeamHomeContainer';
import TeamMembersContainer from 'team/pages/TeamMembersContainer';
import TeamSubteamsContainer from 'team/pages/TeamSubteamsContainer';
import TeamTemplatesContainer from 'team/pages/TeamTemplatesContainer';
import TeamTemplateEditContainer from 'team/pages/TeamTemplateEditContainer';
import TeamAddMemberContainer from 'team/pages/TeamAddMemberContainer';
import TeamLeadsContainer from 'team/pages/TeamLeadsContainer';
import TeamImportLeadsContainer from 'team/pages/TeamImportLeadsContainer';
import TeamCampaignsContainer from 'team/pages/TeamCampaignsContainer';
import TeamInsightsContainer from 'team/pages/TeamInsightsContainer';
import TeamIntegrationsContainer from 'team/pages/TeamIntegrationsContainer';
import TeamEmailSignatureContainer from 'team/pages/TeamEmailSignatureContainer';
import TeamAdminAssetsContainer from 'team/pages/TeamAdminAssetsContainer';
import TeamAdminApiKeysContainer from 'team/pages/TeamAdminApiKeysContainer';
import TeamAdminRequestsContainer from 'team/pages/TeamAdminRequestsContainer';
import TeamAdminInviteEmailsContainer from 'team/pages/TeamAdminInviteEmailsContainer';
import TeamAdminNotificationsContainer from 'team/pages/TeamAdminNotificationsContainer';
import TeamAdminPasswordResetContainer from 'team/pages/TeamAdminPasswordResetContainer';
import TeamSupportContainer from 'team/pages/TeamSupportContainer';
import TeamSettingsContainer from 'team/pages/TeamSettingsContainer';
import TeamSubscriptionContainer from 'team/pages/TeamSubscriptionContainer';
import TeamMemberEditContainer from 'team/pages/TeamMemberEditContainer';
import TeamSubteamMembersContainer from 'team/pages/TeamSubteamMembersContainer';

const TeamDashboard = () => {
    const { authState } = useAuth();
    const { getCurrentTeam, currentTeamId } = useTeam();
    const location = useLocation();
    const navigate = useNavigate();

    // Check if we're on a root-level template route (/template/*)
    // Check location.pathname directly since useMatch won't work for routes defined in App.js
    // Handle both /template/create and /template/:templateId (with or without query strings)
    const isRootTemplateRoute = location.pathname.startsWith('/template/');

    // Get team data
    const teamData = getCurrentTeam();
    console.log('🔍 TeamDashboard - teamData:', teamData);
    console.log('🔍 TeamDashboard - currentTeamId:', currentTeamId);

    // State to track the selected menu
    const [selectedMenu, setSelectedMenu] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Check authentication and redirect if not authenticated
    useEffect(() => {
        if (!authState.isAuthenticated) {
            navigate('/sign-in');
            return;
        }

        // Add a small delay to ensure all context updates are processed
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, [authState.isAuthenticated, navigate]);

    // Effect to check the current URL and set the selected menu accordingly
    useEffect(() => {
        if (location.pathname.includes('/team/home')) {
            setSelectedMenu('home');
        } else if (location.pathname.includes('/team/members')) {
            setSelectedMenu('team');
        } else if (location.pathname.includes('/team/subteams')) {
            setSelectedMenu('team');
        } else if (location.pathname.includes('/team/templates') || location.pathname.includes('/team/template') || location.pathname.includes('/template/')) {
            setSelectedMenu('team');
        } else if (location.pathname.includes('/team/add-members')) {
            setSelectedMenu('team');
        } else if (location.pathname.includes('/team/leads/import-options')) {
            setSelectedMenu('leads');
        } else if (location.pathname.includes('/team/leads')) {
            setSelectedMenu('leads');
        } else if (location.pathname.includes('/team/campaigns')) {
            setSelectedMenu('campaigns');
        } else if (location.pathname.includes('/team/insights')) {
            setSelectedMenu('insights');
        } else if (location.pathname.includes('/team/integrations')) {
            setSelectedMenu('integrations');
        } else if (location.pathname.includes('/team/email-signature')) {
            setSelectedMenu('email-signature');
        } else if (location.pathname.includes('/team/team-assets')) {
            setSelectedMenu('admin');
        } else if (location.pathname.includes('/team/api-keys')) {
            setSelectedMenu('admin');
        } else if (location.pathname.includes('/team/requests')) {
            setSelectedMenu('admin');
        } else if (location.pathname.includes('/team/invite-emails')) {
            setSelectedMenu('admin');
        } else if (location.pathname.includes('/team/notifications')) {
            setSelectedMenu('admin');
        } else if (location.pathname.includes('/team/password-reset')) {
            setSelectedMenu('admin');
        } else if (location.pathname.includes('/team/support')) {
            setSelectedMenu('support');
        } else if (location.pathname.includes('/team/settings')) {
            setSelectedMenu('settings');
        } else if (location.pathname.includes('/team/subscription')) {
            setSelectedMenu('settings');
        } else {
            setSelectedMenu('home');
        }
    }, [location.pathname]);

    // Menu click handler
    const onMenuClick = (menu) => {
        setSelectedMenu(menu);

        switch (menu) {
            case 'home':
                navigate('/team/home');
                break;
            case 'team':
                navigate('/team/members');
                break;
            case 'team-members':
                navigate('/team/members');
                break;
            case 'team-subteams':
                navigate('/team/subteams');
                break;
            case 'team-templates':
                navigate('/team/templates');
                break;
            case 'team-add-members':
                navigate('/team/add-members');
                break;
            case 'leads':
                navigate('/team/leads');
                break;
            case 'leads-import':
                navigate('/team/leads/import-options');
                break;
            case 'campaigns':
                navigate('/team/campaigns');
                break;
            case 'insights':
                navigate('/team/insights');
                break;
            case 'integrations':
                navigate('/team/integrations');
                break;
            case 'email-signature':
                navigate('/team/email-signature');
                break;
            case 'admin':
                navigate('/team/team-assets');
                break;
            case 'admin-assets':
                navigate('/team/team-assets');
                break;
            case 'admin-api-keys':
                navigate('/team/api-keys');
                break;
            case 'admin-requests':
                navigate('/team/requests');
                break;
            case 'admin-invite-emails':
                navigate('/team/invite-emails');
                break;
            case 'admin-notifications':
                navigate('/team/notifications');
                break;
            case 'admin-password-reset':
                navigate('/team/password-reset');
                break;
            case 'support':
                navigate('/team/support');
                break;
            case 'settings':
                navigate('/team/settings');
                break;
            case 'settings-team':
                navigate('/team/team-settings');
                break;
            case 'settings-subscription':
                navigate('/team/subscription');
                break;
            default:
                navigate('/team/home');
        }
    };



    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <TeamSidenav selectedMenu={selectedMenu} onMenuClick={onMenuClick} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 1,
                    backgroundColor: '#f7f7f7',
                    minHeight: '100vh',
                    boxSizing: 'border-box',
                    width: 0, // Prevent flex item from overflowing
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
                        {/* Handle root-level template routes (/template/*) */}
                        {isRootTemplateRoute ? (
                            <TeamTemplateEditContainer user={authState.user} team={teamData} />
                        ) : (
                            <Routes>
                                <Route path="home" element={<TeamHomeContainer user={authState.user} team={teamData} />} />
                                <Route path="members" element={<TeamMembersContainer user={authState.user} team={teamData} />} />
                                <Route path="members/edit/:memberId" element={<TeamMemberEditContainer user={authState.user} team={teamData} />} />
                                <Route path="members/card/:cardId" element={<TeamMemberEditContainer user={authState.user} team={teamData} />} />
                                <Route path="members/card/add-card/:cardId" element={<TeamMemberEditContainer user={authState.user} team={teamData} />} />
                                <Route path="subteams" element={<TeamSubteamsContainer user={authState.user} team={teamData} />} />
                                <Route path="subteams/:subteamId/members" element={<TeamSubteamMembersContainer user={authState.user} team={teamData} />} />
                                <Route path="templates" element={<TeamTemplatesContainer user={authState.user} team={teamData} />} />
                                {/* Template routes - handle /team/template/* (root-level /template/* handled above) */}
                                <Route path="template/create" element={<TeamTemplateEditContainer user={authState.user} team={teamData} />} />
                                <Route path="template/:templateId" element={<TeamTemplateEditContainer user={authState.user} team={teamData} />} />
                                <Route path="add-members" element={<TeamAddMemberContainer user={authState.user} team={teamData} />} />
                                <Route path="leads" element={<TeamLeadsContainer user={authState.user} team={teamData} />} />
                                <Route path="leads/import-options" element={<TeamImportLeadsContainer user={authState.user} team={teamData} />} />
                                <Route path="campaigns" element={<TeamCampaignsContainer user={authState.user} team={teamData} />} />
                                <Route path="insights" element={<TeamInsightsContainer user={authState.user} team={teamData} />} />
                                <Route path="integrations" element={<TeamIntegrationsContainer user={authState.user} team={teamData} />} />
                                <Route path="email-signature" element={<TeamEmailSignatureContainer user={authState.user} team={teamData} />} />
                                <Route path="team-assets" element={<TeamAdminAssetsContainer user={authState.user} team={teamData} />} />
                                <Route path="api-keys" element={<TeamAdminApiKeysContainer user={authState.user} team={teamData} />} />
                                <Route path="requests" element={<TeamAdminRequestsContainer user={authState.user} team={teamData} />} />
                                <Route path="invite-emails" element={<TeamAdminInviteEmailsContainer user={authState.user} team={teamData} />} />
                                <Route path="notifications" element={<TeamAdminNotificationsContainer user={authState.user} team={teamData} />} />
                                <Route path="password-reset" element={<TeamAdminPasswordResetContainer user={authState.user} team={teamData} />} />
                                <Route path="support" element={<TeamSupportContainer user={authState.user} team={teamData} />} />
                                <Route path="settings" element={<TeamSettingsContainer user={authState.user} team={teamData} />} />
                                <Route path="team-settings" element={<TeamSettingsContainer user={authState.user} team={teamData} />} />
                                <Route path="subscription" element={<TeamSubscriptionContainer user={authState.user} team={teamData} />} />
                                <Route path="*" element={<TeamHomeContainer user={authState.user} team={teamData} />} />
                            </Routes>
                        )}
                    </Box>
                )}
                <Outlet />
            </Box>
        </Box>
    );
};

export default TeamDashboard;
